import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};

export const useStakeSelector = () => {
  const [decayLots, setDecayLots] = useState([]);
  const [trustedTokens, setTrustedTokens] = useState([]);
  const [lotStakes, setLotStakes] = useState({});
  const [trustStakes, setTrustStakes] = useState({});
  const [totalStake, setTotalStake] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTokens = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const API_URL = getApiUrl();
        
        const [lotsResponse, trustsResponse] = await Promise.all([
          axios.get(`${API_URL}/api/wallets/decay-lots`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/trust/received/available`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (lotsResponse.data.success) {
          setDecayLots(lotsResponse.data.lots || []);
        }
        
        if (trustsResponse.data.success) {
          setTrustedTokens(trustsResponse.data.data.available_trusts || []);
        }
      } catch (err) {
        console.error('トークン取得エラー:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTokens();
  }, []);

  const distributeStake = useCallback((total) => {
    const newLotStakes = {};
    const newTrustStakes = {};
    let remaining = total;
    
    const sortedLots = [...decayLots].sort((a, b) => {
      const dateA = a.decay_end_date ? new Date(a.decay_end_date) : new Date('9999-12-31');
      const dateB = b.decay_end_date ? new Date(b.decay_end_date) : new Date('9999-12-31');
      return dateA - dateB;
    });
    
    for (const lot of sortedLots) {
      if (remaining <= 0) break;
      
      const needed = lot.current_amount || 0;
      const allocated = Math.min(needed, remaining);
      newLotStakes[lot.transaction_id] = allocated;
      remaining -= allocated;
    }
    
    for (const trust of trustedTokens) {
      if (remaining <= 0) break;
      
      const needed = trust.available_amount || 0;
      const allocated = Math.min(needed, remaining);
      const key = `trust_${trust.truster_actor_id}_${trust.token_type}`;
      newTrustStakes[key] = allocated;
      remaining -= allocated;
    }
    
    setLotStakes(newLotStakes);
    setTrustStakes(newTrustStakes);
  }, [decayLots, trustedTokens]);

  // 総量変更ハンドラ
  const handleTotalStakeChange = useCallback((value, skipDistribute = false) => {
    const numValue = parseFloat(value) || 0;
    setTotalStake(numValue);
    if (!skipDistribute) {
      distributeStake(numValue);
    }
  }, [distributeStake]);

  const handleLotStakeChange = useCallback((lotId, value) => {
    const newLotStakes = { ...lotStakes };
    if (value === 0 || value === '0' || value === '') {
      delete newLotStakes[lotId];
    } else {
      newLotStakes[lotId] = value;
    }
    
    setLotStakes(newLotStakes);
    
    const newTotal = 
      Object.values(newLotStakes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0) +
      Object.values(trustStakes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    setTotalStake(newTotal);
  }, [lotStakes, trustStakes]);

  const handleTrustStakeChange = useCallback((trustKey, value) => {
    const newTrustStakes = { ...trustStakes };
    if (value === 0 || value === '0' || value === '') {
      delete newTrustStakes[trustKey];
    } else {
      newTrustStakes[trustKey] = value;
    }
    
    setTrustStakes(newTrustStakes);
    
    const newTotal = 
      Object.values(lotStakes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0) +
      Object.values(newTrustStakes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    setTotalStake(newTotal);
  }, [lotStakes, trustStakes]);

  const applyFullStake = useCallback(() => {
    const newLotStakes = {};
    const newTrustStakes = {};
    let total = 0;
    
    decayLots.forEach(lot => {
      const needed = lot.current_amount || 0;
      newLotStakes[lot.transaction_id] = needed;
      total += needed;
    });

    trustedTokens.forEach(trust => {
      const needed = trust.available_amount || 0;
      const key = `trust_${trust.truster_actor_id}_${trust.token_type}`;
      newTrustStakes[key] = needed;
      total += needed;
    });
    
    setLotStakes(newLotStakes);
    setTrustStakes(newTrustStakes);
    setTotalStake(total);
  }, [decayLots, trustedTokens]);

  const resetStake = useCallback(() => {
    setLotStakes({});
    setTrustStakes({});
    setTotalStake('');
  }, []);

  return {
    decayLots,
    trustedTokens,
    lotStakes,
    trustStakes,
    totalStake,
    loading,
    error,
    handleTotalStakeChange,
    handleLotStakeChange,
    handleTrustStakeChange,
    applyFullStake,
    resetStake,
  };
};
